import React, {useMemo} from 'react';
import {
    Alert, Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography
} from '@mui/material';

/**
 * Live preview of the dice rolling combo probabilities derived from
 * `config.combinations[*].weight` (v2 algorithm).
 *
 * Math (mirrors gcp-core/cloud-functions/minigame-dice-rolling-cf/lib/DiceRollingHandler.js):
 *   - totalWeight = Σ combinations[*].weight
 *   - If totalWeight <= 100:
 *       each combo's rate = weight%, no-combo rate = (100 - totalWeight)%
 *   - If totalWeight > 100:
 *       each combo's rate = (weight / totalWeight) × 100 %, no-combo impossible
 */
function DiceProbabilityPreview({config}) {
    const {rows, totalWeight, noMatchPct, normalized} = useMemo(() => {
        const combos = (config && config.combinations) || {};
        const entries = Object.entries(combos).map(([key, c]) => ({
            key,
            displayName: (c && c.display_name) || key,
            weight: Number(c && c.weight) || 0,
        }));
        const total = entries.reduce((a, b) => a + b.weight, 0);
        const isNormalized = total > 100;
        const scale = isNormalized ? total : 100;
        const noMatch = isNormalized ? 0 : (100 - total);
        const rowsOut = entries.map(e => ({
            ...e,
            pct: scale > 0 ? (e.weight / scale) * 100 : 0,
        }));
        return {rows: rowsOut, totalWeight: total, noMatchPct: noMatch, normalized: isNormalized};
    }, [config]);

    if (!rows.length) {
        return (
            <Alert severity="info" sx={{mt: 1}}>
                Add combinations with <code>weight</code> fields to see probability preview.
            </Alert>
        );
    }

    const fmt = (x) => `${x.toFixed(2)}%`;
    const per1000 = (pct) => Math.round(pct * 10);

    return (
        <Paper variant="outlined" sx={{p: 2, mt: 2}}>
            <Typography variant="subtitle1" sx={{mb: 1}}>
                Probability preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Σweight = {totalWeight}. {normalized
                    ? `Sum exceeds 100 — weights normalized; no-combo impossible.`
                    : `Sum ≤ 100 — weight value equals direct percentage; remainder is no-combo.`}
            </Typography>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Combination</TableCell>
                            <TableCell align="right">Weight</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Per 1000 rolls</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.key}>
                                <TableCell>
                                    <Box component="span" sx={{fontFamily: 'monospace', mr: 1}}>{r.key}</Box>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {r.displayName !== r.key ? `(${r.displayName})` : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{r.weight}</TableCell>
                                <TableCell align="right">{fmt(r.pct)}</TableCell>
                                <TableCell align="right">{per1000(r.pct)}</TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{fontWeight: 'bold', '& td': {fontWeight: 'bold', borderTop: '2px solid', borderTopColor: 'divider'}}}>
                            <TableCell>Σ combo</TableCell>
                            <TableCell align="right">{totalWeight}</TableCell>
                            <TableCell align="right">{fmt(normalized ? 100 : totalWeight)}</TableCell>
                            <TableCell align="right">{per1000(normalized ? 100 : totalWeight)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>No-combo (consolation)</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">{fmt(noMatchPct)}</TableCell>
                            <TableCell align="right">{per1000(noMatchPct)}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {normalized && (
                <Alert severity="warning" sx={{mt: 2}}>
                    Σweight = {totalWeight} exceeds 100. The CF will normalize proportionally
                    so every roll produces a combo (no consolation is ever drawn).
                </Alert>
            )}
            {totalWeight === 0 && (
                <Alert severity="info" sx={{mt: 2}}>
                    All weights are zero — every roll will be no-combo.
                </Alert>
            )}
        </Paper>
    );
}

/** Optional validator for use in form submit. Returns null if valid, else error message. */
DiceProbabilityPreview.validate = (config) => {
    const combos = (config && config.combinations) || {};
    for (const [key, c] of Object.entries(combos)) {
        if (!c || typeof c !== 'object') return `combinations["${key}"] must be an object`;
        const w = Number(c.weight);
        if (!Number.isFinite(w) || w < 0) {
            return `combinations["${key}"].weight must be a finite number >= 0 (got ${c.weight})`;
        }
    }
    return null;
};

export default DiceProbabilityPreview;
